import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { Logo } from "./Logo";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

// Public, tokenized view of one booking's delivered photos.
export default function PhotoGallery() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [files, setFiles] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/gallery/${bookingId}/files?token=${encodeURIComponent(token)}`
        );
        if (active) setFiles(data.files || []);
      } catch (e) {
        if (active)
          setErr(
            e?.response?.data?.detail ||
              "This gallery link isn't valid — email GameLensPhotos@gmail.com for a fresh one."
          );
      }
    })();
    return () => {
      active = false;
    };
  }, [bookingId, token]);

  return (
    <div className="min-h-screen bg-ink font-body text-white" data-testid="gallery-page">
      <header className="border-b border-white/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-6">
          <span className="text-accentlight">
            <Logo className="h-8 w-8" />
          </span>
          <div className="leading-none">
            <p className="font-display text-xl tracking-wider">
              THE <span className="text-accentlight">GALLERY</span>
            </p>
            <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.4em] text-white/40">
              GameLens Photography
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        {err && (
          <p
            className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
            data-testid="gallery-error"
          >
            {err}
          </p>
        )}

        {!files && !err && (
          <p className="flex items-center justify-center gap-3 py-24 text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
            <Loader2 className="h-5 w-5 animate-spin text-accentlight" /> Opening your gallery…
          </p>
        )}

        {files && files.length === 0 && !err && (
          <p className="py-24 text-center text-white/50" data-testid="gallery-empty">
            Your photos are still being finished — check back soon.
          </p>
        )}

        {files && files.length > 0 && (
          <>
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <h1 className="font-display text-4xl tracking-wider md:text-5xl">
                YOUR <span className="text-accentlight">PHOTOS</span>
              </h1>
              <div className="flex flex-wrap items-center gap-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-testid="gallery-count">
                  {files.length} photo{files.length === 1 ? "" : "s"} — tap any to view or download
                </p>
                <a
                  href={`${BASE}/api/gallery/${bookingId}/zip?token=${encodeURIComponent(token)}`}
                  download
                  data-testid="gallery-download-all"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110"
                >
                  <Download className="h-4 w-4" /> Download all (ZIP)
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {files.map((f) => (
                <a
                  key={f.id}
                  href={`${BASE}${f.url}`}
                  target="_blank"
                  rel="noreferrer"
                  download={f.filename}
                  data-testid={`gallery-photo-${f.id}`}
                  className="group relative block aspect-square overflow-hidden rounded-xl border border-white/5"
                >
                  <img
                    src={`${BASE}${f.thumb_url || f.url}`}
                    alt={f.filename}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-2 right-2 rounded-full bg-ink/80 p-2 text-white/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <Download className="h-4 w-4" />
                  </span>
                </a>
              ))}
            </div>
            <p className="mt-10 text-center text-xs text-white/30">
              These photos are private to your family — share the joy, not the link.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
